import React from "react";
import { Link } from "react-scroll";
import { motion } from "framer-motion";
import GlobalStyles from "./GlobalStyles";
import { FaFileAlt } from "react-icons/fa";
import profilePic from "./assets/profile.jpg";
import resume from "./assets/Dominic_Musumeci_Resume.pdf";
import { Analytics } from "@vercel/analytics/react"

import { 
  Navbar, 
  NavLinks, 
  Hero, 
  HeroText, 
  HeroImage, 
  ButtonContainer, 
  ResumeButton, 
  ContactButton, 
  AboutSection, 
  ProjectsSection, 
  ContactSection, 
  FormContainer,
  InputContainer,
  InputField,
  MessageBox,
  SendButton,
  ProjectWrapper,
  ProjectCard
} from "./styles";

const App = () => {
  return (
    <>
      <GlobalStyles />
      
      {/* Navigation Bar */}
      <Navbar>
        <NavLinks>
          <Link to="home" smooth={true} duration={600} offset={-50}>Home</Link>
          <Link to="about" smooth={true} duration={600} offset={-50}>About</Link>
          <Link to="projects" smooth={true} duration={600} offset={-50}>Projects</Link>
          <Link to="contact" smooth={true} duration={600} offset={-50}>Contact</Link>
        </NavLinks>
      </Navbar>

      {/* Hero Section (Home) */}
      <Hero id="home">
        <HeroText>
          <motion.h1 
            initial={{ opacity: 0, y: -30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }}
          >
            DOMINIC MUSUMECI
          </motion.h1>

          <motion.h2 
            initial={{ opacity: 0, y: -20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            ML & AI Enthusiast
          </motion.h2>

          <motion.p 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Computer Science & Mathematics <br />
            <span style={{ color: "#ffcc00", fontWeight: "bold" }}>@ University of Rochester</span>
          </motion.p>

          <ButtonContainer>
            <ResumeButton href={resume} target="_blank">
              <FaFileAlt /> Resume
            </ResumeButton>
            <ContactButton to="contact" smooth={true} duration={600}>
              Contact Me
            </ContactButton>
          </ButtonContainer>
        </HeroText>

        <HeroImage 
          src={profilePic} 
          alt="Dominic Musumeci" 
          initial={{ opacity: 0, scale: 0.9 }} 
          animate={{ opacity: 1, scale: 1 }} 
          transition={{ duration: 0.6, delay: 0.4 }} 
        />
      </Hero>

      {/* About Section */}
      <AboutSection id="about">
        <motion.h2 
          initial={{ opacity: 0, y: 30 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6 }}
        >
          About Me
        </motion.h2>

        <motion.p 
          initial={{ opacity: 0, y: 30 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Hey, I’m Dominic! I’m from <strong>Syracuse, NY</strong>, and I have a deep passion for 
          <strong> machine learning, deep learning, NLP, and probability theory</strong>. I’m always looking for ways 
          to push the boundaries of AI and gain a deeper understanding of how models learn and make decisions. 
          Recently, I’ve been exploring <strong>quantitative finance</strong>, and I even took an 
          <strong> Introduction to Quantitative Finance</strong> course to get a solid foundation in the field.
        </motion.p>

        <motion.p 
          initial={{ opacity: 0, y: 30 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          Outside of academics, you’ll probably find me <strong>weightlifting</strong> (I love leg day), 
          <strong> playing basketball </strong> (huge OKC Thunder fan), or <strong>building custom PCs</strong>. 
          I also love <strong> gaming</strong> and analyzing <strong>game theory optimal (GTO) strategies in card games</strong>.
        </motion.p>
        <motion.p 
        initial={{ opacity: 0, y: 30 }} 
        whileInView={{ opacity: 1, y: 0 }} 
        transition={{ duration: 0.6, delay: 0.6 }}
        >
          Whether it's optimizing a deep learning model or fine-tuning a strategy in a game, I love solving 
          complex problems and finding creative solutions. If you ever want to chat about <strong>AI, finance, lifting, 
          or basketball</strong>, I’m always down!
        </motion.p>
      </AboutSection>

      {/* Projects Section */}
      <ProjectsSection id="projects">
        <motion.h2 
          initial={{ opacity: 0, y: 30 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6 }}
        >
          Featured Projects
        </motion.h2>

        <ProjectWrapper>
          {[
            // {
            //   title: "Contrastive Temporal Graph Transformers",
            //   description: "Designed a Video Graph Transformer (VGT) to analyze time-based relationships in videos.",
            //   link: "contrastive_temporal_graph_transformers.pdf",
            //   delay: 0.2
            // },
            {
              title: "Predictive Modeling for Cardiovascular Disease",
              description: "Built a model using deep learning, gradient boosting, and BERT embeddings to achieve over 73% accuracy.",
              link: "predictive_modeling_cvd.pdf",
              delay: 0.4
            },
            {
              title: "Mars Rover App",
              description: "Developed an iOS app using Swift and NASA's API to fetch Mars rover images.",
              link: "mars_rover_app.pdf",
              delay: 0.6
            },
            {
              title: "Measure Theory & Probability Spaces",
              description: "Explores measure theory and probability concepts like Lebesgue integration and sigma algebras.",
              link: "intro_to_prob_theory.pdf",
              delay: 0.8
            }
          ].map((project, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 30 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: project.delay }}
            >
              <ProjectCard 
                href={`${window.location.origin}/assets/${project.link}`} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <h3>{project.title}</h3>
                <p>{project.description}</p>
              </ProjectCard>
            </motion.div>
          ))}
        </ProjectWrapper>
      </ProjectsSection>

      {/* Contact Section */}
      <ContactSection id="contact">
        <motion.h2 
          initial={{ opacity: 0, y: 30 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6 }}
        >
          Let's Get in Touch!
        </motion.h2>

        <FormContainer 
          initial={{ opacity: 0, y: 30 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6, delay: 0.2 }}
          action="https://formsubmit.co/DominicMusumeci2022@gmail.com" 
          method="POST"
        >
          <InputContainer>
            <InputField type="text" name="name" placeholder="Your Name" required />
            <InputField type="text" name="subject" placeholder="Subject" required />
          </InputContainer>

          <InputField type="email" name="email" placeholder="Your Email" required />
          <MessageBox name="message" placeholder="Your Message" required></MessageBox>

          <SendButton type="submit">Send Message</SendButton>
        </FormContainer>
      </ContactSection>
      <Analytics />
    </>
  );
};

export default App;
